/** Canonical customer-facing policy routes for the website and Customer App. */
export const CUSTOMER_POLICY_ROUTES = {
  terms: '/terms-and-conditions',
  privacy: '/privacy-policy',
  booking: '/booking-policy',
  cancellation: '/cancellation-refund-policy',
  fare: '/fare-payment-policy',
  accountDeletion: '/customer-account-deletion',
};

export const CUSTOMER_POLICY_URLS = {
  terms: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.terms}`,
  privacy: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.privacy}`,
  booking: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.booking}`,
  cancellation: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.cancellation}`,
  fare: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.fare}`,
  accountDeletion: `https://easydroptaxi.com${CUSTOMER_POLICY_ROUTES.accountDeletion}`,
};

export const CUSTOMER_SUPPORT = {
  phoneDisplay: '+91 9629954544',
  phoneTel: 'tel:+919629954544',
  email: 'support@easydroptaxi.com',
  mailto: 'mailto:support@easydroptaxi.com',
};

export const CUSTOMER_POLICY_PAGES = [
  {
    key: 'terms',
    path: CUSTOMER_POLICY_ROUTES.terms,
    title: 'EasyDropTaxi Terms & Conditions',
    heading: 'Terms & Conditions',
    description:
      'Terms for booking EasyDropTaxi customer rides on the website and Customer App, including booking requests, passenger conduct, and support.',
    lastUpdated: '9 October 2026',
    sections: [
      {
        heading: 'About these terms',
        paragraphs: [
          'These Terms & Conditions explain how EasyDropTaxi customer bookings work on the EasyDropTaxi website and Customer App. They apply to customers requesting a taxi through EasyDropTaxi.',
          'They do not replace the separate Driver App Terms & Conditions used by authorized EasyDropTaxi drivers.',
        ],
      },
      {
        heading: 'Booking requests',
        paragraphs: [
          'A booking submitted through EasyDropTaxi is a request until EasyDropTaxi reviews and confirms it.',
          'Estimated fare, vehicle category, pickup, drop, date, and time are shown before you confirm a request.',
        ],
      },
      {
        heading: 'Passenger conduct',
        list: [
          'No smoking inside the vehicle',
          'Respect the driver and vehicle',
          'No consumption of alcohol inside the vehicle',
          'Keep the vehicle clean',
        ],
      },
      {
        heading: 'Payment & safety',
        list: [
          'Payment through multiple modes is accepted as arranged for the trip',
          'A receipt is issued for transactions where applicable',
          'Driver identity and vehicle number are verified as part of EasyDropTaxi operations',
          'Emergency support is available 24/7 through EasyDropTaxi support channels',
        ],
      },
      {
        heading: 'Related policies',
        paragraphs: [
          'For booking access and My Bookings rules, see the Booking Policy. For cancellation handling, see the Cancellation & Refund Policy. For fare calculation and extras, see the Fare & Payment Policy. For personal data and account deletion, see the Privacy Policy and Customer Account & Data Deletion page.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'For questions about these terms, contact EasyDropTaxi support.',
        ],
      },
    ],
  },
  {
    key: 'privacy',
    path: CUSTOMER_POLICY_ROUTES.privacy,
    title: 'EasyDropTaxi Privacy Policy',
    heading: 'Privacy Policy',
    description:
      'Privacy information for EasyDropTaxi website and Customer App customers, including booking data, Google Sign-In, notifications, and account deletion.',
    lastUpdated: '9 October 2026',
    sections: [
      {
        heading: 'Scope',
        paragraphs: [
          'This Privacy Policy explains how EasyDropTaxi handles customer information for the EasyDropTaxi website and Customer App.',
          'It does not replace the Driver App Privacy Policy used for authorized EasyDropTaxi drivers.',
        ],
      },
      {
        heading: 'Information we may use',
        paragraphs: [
          'Depending on how you book or use the Customer App, EasyDropTaxi may process information needed to fulfil bookings and provide support, including:',
        ],
        list: [
          'Name and contact mobile number',
          'Email address when you sign in with Google or request account deletion',
          'Pickup, drop, date, time, vehicle choice, and passenger details for a booking',
          'Booking reference, trip status, fare, and cancellation records',
          'Google account identifiers used for Customer App sign-in',
          'Notification tokens and inbox items used to send booking updates when notifications are enabled',
        ],
      },
      {
        heading: 'Google Sign-In',
        paragraphs: [
          'The Customer App uses Google Sign-In so you can view bookings linked to your account and manage your EasyDropTaxi profile.',
          'EasyDropTaxi never stores your Google password.',
        ],
      },
      {
        heading: 'How information is used',
        list: [
          'To create and fulfil taxi booking requests',
          'To show bookings in My Bookings for signed-in or guest customers',
          'To contact you about a booking through EasyDropTaxi support channels',
          'To send booking-related notifications when enabled',
          'To process Customer App account deletion requests',
        ],
      },
      {
        heading: 'Account deletion',
        paragraphs: [
          'You can delete a Customer App account in the app under Account, or through the public Customer Account & Data Deletion page.',
          'Deletion removes the Google sign-in account, customer profile, notification tokens, and notification inbox for that account.',
          'Trip, fare, and cancellation records may be retained for operations and accounting with personal fields such as name and mobile number removed.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'For privacy questions, contact EasyDropTaxi support.',
        ],
      },
    ],
  },
  {
    key: 'booking',
    path: CUSTOMER_POLICY_ROUTES.booking,
    title: 'EasyDropTaxi Booking & My Bookings Policy',
    heading: 'Booking & My Bookings Policy',
    description:
      'How EasyDropTaxi customer bookings work, including confirmation, My Bookings access for Google and guest customers, and trip changes.',
    lastUpdated: '9 October 2026',
    sections: [
      {
        heading: 'Booking requests',
        paragraphs: [
          'Bookings submitted through EasyDropTaxi are requests until EasyDropTaxi confirms them.',
          'Estimated fare is shown before confirmation. Actual trip handling follows EasyDropTaxi operations after review.',
        ],
      },
      {
        heading: 'My Bookings for signed-in customers',
        paragraphs: [
          'Signed-in Customer App users can see bookings linked to their Google account in My Bookings.',
          'Booking status, driver details after assignment, and support options appear on Booking Details.',
        ],
      },
      {
        heading: 'Guest bookings',
        paragraphs: [
          'You can book without signing in.',
          'Guest customers can find a booking in My Bookings by entering the Booking ID and the mobile number used for that booking.',
        ],
      },
      {
        heading: 'Changes to an existing booking',
        paragraphs: [
          'Changes to pickup, drop, date, time, vehicle, or passenger details on an existing booking are handled by EasyDropTaxi Support so fare, availability, and assignment can be validated.',
          'Direct unrestricted editing of confirmed bookings is not available in the current Customer App version.',
        ],
      },
      {
        heading: 'Related policies',
        paragraphs: [
          'Cancellation handling is described in the Cancellation & Refund Policy. Fare estimates and extras are described in the Fare & Payment Policy.',
        ],
      },
    ],
  },
  {
    key: 'cancellation',
    path: CUSTOMER_POLICY_ROUTES.cancellation,
    title: 'EasyDropTaxi Cancellation & Refund Policy',
    heading: 'Cancellation & Refund Policy',
    description:
      'How EasyDropTaxi handles customer cancellation requests based on booking status, driver assignment, and trip progress.',
    lastUpdated: '9 October 2026',
    sections: [
      {
        heading: 'Current cancellation handling',
        paragraphs: [
          'Cancellation options depend on the current status of your booking. The Customer App and EasyDropTaxi support tools apply the following handling:',
        ],
        list: [
          'Before driver assignment — you can cancel with no cancellation fee under the current policy',
          'After driver assignment — a cancellation request is reviewed according to the configured policy; a charge may apply when configured',
          'When the driver is on the way — contact EasyDropTaxi support for cancellation help',
          'After the trip starts — customer cancellation is unavailable; contact support if you need help',
          'Cancelled, completed, or unable-to-confirm bookings — cancellation is not available',
        ],
      },
      {
        heading: 'How to cancel',
        paragraphs: [
          'Signed-in and guest customers can open the booking in My Bookings and use the cancellation option when it is available for that booking status.',
          'You can also contact EasyDropTaxi support by phone or WhatsApp for help with a booking.',
        ],
      },
      {
        heading: 'Fees and refunds',
        paragraphs: [
          'Before driver assignment, the current policy applies no cancellation fee.',
          'After driver assignment, any applicable charge follows the configured cancellation setting for that booking and is shown in the cancellation flow when a charge may apply.',
          'This page does not promise a fixed refund percentage for every booking. Final handling follows the booking status rules above and EasyDropTaxi support review where required.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'For cancellation help, open the booking in My Bookings or contact EasyDropTaxi support.',
        ],
      },
    ],
  },
  {
    key: 'fare',
    path: CUSTOMER_POLICY_ROUTES.fare,
    title: 'EasyDropTaxi Fare & Payment Policy',
    heading: 'Fare & Payment Policy',
    description:
      'How EasyDropTaxi estimated fares are calculated, including vehicle rates, driver bata, minimum billing distance, extras, and peak pricing.',
    lastUpdated: '9 October 2026',
    sections: [
      {
        heading: 'How estimated fare is calculated',
        paragraphs: [
          'Estimated fare is calculated from billing distance × vehicle rate + driver bata using current EasyDropTaxi backend settings.',
          'Published base vehicle rates currently used on the website tariff board are Sedan ₹15/km, SUV ₹20/km, and Crysta ₹22/km. Driver bata and minimum billing settings are controlled by EasyDropTaxi configuration.',
        ],
      },
      {
        heading: 'Billing distance',
        paragraphs: [
          'One-way trips may use a configured minimum billing distance from backend settings. A common configured minimum is 130 km.',
          'Billing distance can be higher than the mapped route distance when the minimum applies.',
        ],
      },
      {
        heading: 'Extra charges',
        paragraphs: [
          'Toll, parking, permit, hill, waiting, and similar charges may be extra when applicable, unless a booking record states otherwise.',
        ],
      },
      {
        heading: 'Peak and festival pricing',
        paragraphs: [
          'During festivals, public holidays, peak travel periods, and high-demand times, vehicle fares may be higher than normal rates.',
          'The fare shown before you confirm is based on the current backend pricing for your booking date, route, and vehicle.',
        ],
      },
      {
        heading: 'Payment',
        paragraphs: [
          'Payment is arranged according to the booking and EasyDropTaxi operations. Multiple payment modes may be accepted as arranged for the trip.',
          'A receipt is issued for transactions where applicable.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: [
          'For fare questions about a booking, use Fare Help in the Customer App or contact EasyDropTaxi support.',
        ],
      },
    ],
  },
];
